import { collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { BookingFormConfig, Booking } from '../types/bookingTypes';
import { resolveBookingPhone } from '../utils/bookingPhone';

const mapBookingDocument = (id: string, data: Record<string, any>): Booking => ({
  id,
  ...data,
  // The original booking form saved this value as `contact`, while the newer
  // form saves it as `phone`. Normalize both formats for every dashboard view.
  phone: resolveBookingPhone(data),
  createdAt: data.createdAt?.toDate?.() || new Date(),
  updatedAt: data.updatedAt?.toDate?.() || new Date(),
}) as Booking;

export class BookingService {
  // Collection references
  private configCollection = 'booking-config';
  private bookingsCollection = 'bookings';

  // Get booking form configuration
  async getBookingConfig(): Promise<BookingFormConfig | null> {
    try {
      const configDoc = doc(db, this.configCollection, 'main');
      const docSnap = await getDoc(configDoc);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        console.log('✅ Booking configuration loaded from Firestore:', data);
        return data as BookingFormConfig;
      }
      
      console.log('📄 No booking configuration document found in Firestore');
      return null;
    } catch (error) {
      console.error('Error fetching booking config:', error);
      throw error;
    }
  }

  // Update booking form configuration
  async updateBookingConfig(config: BookingFormConfig): Promise<void> {
    try {
      console.log('💾 Saving booking configuration to Firestore:', config);
      const configDoc = doc(db, this.configCollection, 'main');
      await setDoc(configDoc, config, { merge: true });
      console.log('✅ Booking configuration saved successfully to Firestore');
    } catch (error) {
      console.error('❌ Error updating booking config:', error);
      throw error;
    }
  }

  // Get all bookings
  async getBookings(): Promise<Booking[]> {
    try {
      const snapshot = await getDocs(collection(db, this.bookingsCollection));
      return snapshot.docs.map(doc => mapBookingDocument(doc.id, doc.data()));
    } catch (error) {
      console.error('Error fetching bookings:', error);
      throw error;
    }
  }

  // Update booking status
  async updateBookingStatus(bookingId: string, status: Booking['status']): Promise<void> {
    try {
      const bookingDoc = doc(db, this.bookingsCollection, bookingId);
      await updateDoc(bookingDoc, {
        status,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating booking status:', error);
      throw error;
    }
  }

  // Add, update, or clear an internal booking remark
  async updateBookingRemark(bookingId: string, remark: string): Promise<void> {
    try {
      const bookingDoc = doc(db, this.bookingsCollection, bookingId);
      await updateDoc(bookingDoc, {
        remark,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating booking remark:', error);
      throw error;
    }
  }

  // Delete booking
  async deleteBooking(bookingId: string): Promise<void> {
    try {
      const bookingDoc = doc(db, this.bookingsCollection, bookingId);
      await deleteDoc(bookingDoc);
    } catch (error) {
      console.error('Error deleting booking:', error);
      throw error;
    }
  }

  // Real-time listener for bookings
  onBookingsChange(callback: (bookings: Booking[]) => void): () => void {
    return onSnapshot(collection(db, this.bookingsCollection), (snapshot) => {
      const bookings = snapshot.docs.map(doc => mapBookingDocument(doc.id, doc.data()));
      callback(bookings);
    });
  }

  // Add some mock bookings for testing (Shopify Standard Format)
  async addMockBookings(): Promise<void> {
    const mockBookings = [
      {
        // Customer Information (Shopify Standard)
        firstName: 'Sarah',
        lastName: 'Chen',
        fullName: 'Sarah Chen',
        email: 'sarah.chen@email.com',
        phone: '+60 12-345 6789',
        
        // Service Information (Shopify Standard)
        serviceType: 'wedding',
        serviceName: 'decoration',
        
        // Booking Details (Shopify Standard)
        consultationDate: '2024-02-15',
        consultationTime: '10:00 AM',
        details: 'Looking for elegant white and pink theme decoration for 150 guests',
        
        // System Fields
        status: 'pending' as const,
        createdAt: new Date('2024-01-10'),
        updatedAt: new Date('2024-01-10'),
        
        // Legacy fields for backward compatibility
        name: 'Sarah Chen',
        eventType: 'wedding',
        serviceCategory: 'decoration',
        eventDate: '2024-03-20',
        location: 'kl',
        notes: 'Looking for elegant white and pink theme decoration for 150 guests'
      },
      {
        // Customer Information (Shopify Standard)
        firstName: 'Ahmad',
        lastName: 'Rahman',
        fullName: 'Ahmad Rahman',
        email: 'ahmad.r@company.com',
        phone: '+60 12-987 6543',
        
        // Service Information (Shopify Standard)
        serviceType: 'corporate',
        serviceName: 'decoration',
        
        // Booking Details (Shopify Standard)
        consultationDate: '2024-02-16',
        consultationTime: '2:00 PM',
        details: 'Corporate event for 100 people, need professional setup',
        
        // System Fields
        status: 'confirmed' as const,
        createdAt: new Date('2024-01-11'),
        updatedAt: new Date('2024-01-12'),
        
        // Legacy fields for backward compatibility
        name: 'Ahmad Rahman',
        eventType: 'corporate',
        serviceCategory: 'decoration',
        eventDate: '2024-02-28',
        location: 'pj',
        notes: 'Corporate event for 100 people, need professional setup'
      },
      {
        // Customer Information (Shopify Standard)
        firstName: 'Lisa',
        lastName: 'Wong',
        fullName: 'Lisa Wong',
        email: 'lisa.w@gmail.com',
        phone: '+60 11-234 5678',
        
        // Service Information (Shopify Standard)
        serviceType: 'birthday',
        serviceName: 'dessert',
        
        // Booking Details (Shopify Standard)
        consultationDate: '2024-02-10',
        consultationTime: '4:00 PM',
        details: 'Sweet 16 birthday party, pink and gold theme',
        
        // System Fields
        status: 'completed' as const,
        createdAt: new Date('2024-01-08'),
        updatedAt: new Date('2024-01-15'),
        
        // Legacy fields for backward compatibility
        name: 'Lisa Wong',
        eventType: 'birthday',
        serviceCategory: 'dessert',
        eventDate: '2024-02-25',
        location: 'sa',
        notes: 'Sweet 16 birthday party, pink and gold theme'
      }
    ];

    try {
      for (const booking of mockBookings) {
        await setDoc(doc(collection(db, this.bookingsCollection)), booking);
      }
      console.log('✅ Mock bookings added successfully');
    } catch (error) {
      console.error('❌ Error adding mock bookings:', error);
    }
  }

  // Initialize default configuration
  async initializeDefaultConfig(): Promise<void> {
    try {
      const defaultConfig: BookingFormConfig = {
        eventTypes: [
          { id: 'wedding', name: 'Wedding Event', enabled: true, order: 1 },
          { id: 'corporate', name: 'Corporate Event', enabled: true, order: 2 },
          { id: 'birthday', name: 'Birthday Event', enabled: true, order: 3 },
          { id: 'proposal', name: 'Marriage Proposal', enabled: true, order: 4 },
          { id: 'other', name: 'Other Event', enabled: true, order: 5 }
        ],
        serviceCategories: [
          { 
            id: 'decoration', 
            name: 'Decoration', 
            description: 'Backdrop, walkway, photo taking area',
            enabled: true, 
            onlineOnly: false, 
            order: 1 
          },
          { 
            id: 'accessories', 
            name: 'Accessories Items', 
            description: 'Table setup, table decoration',
            enabled: true, 
            onlineOnly: true, 
            order: 2 
          },
          { 
            id: 'dessert', 
            name: 'Dessert Table Setup', 
            description: 'Cake stand, dessert stand, dessert plate',
            enabled: true, 
            onlineOnly: true, 
            order: 3 
          }
        ],
        timeSlots: [
          { id: 'slot1', time: '10:00 AM', enabled: true, order: 1 },
          { id: 'slot2', time: '2:00 PM', enabled: true, order: 2 },
          { id: 'slot3', time: '4:00 PM', enabled: false, order: 3 },
          { id: 'slot4', time: '6:00 PM', enabled: true, order: 4 }
        ],
        locations: [
          { id: 'kl', name: 'Kuala Lumpur', enabled: true, order: 1 },
          { id: 'pj', name: 'Petaling Jaya', enabled: true, order: 2 },
          { id: 'sa', name: 'Shah Alam', enabled: true, order: 3 },
          { id: 'subang', name: 'Subang', enabled: true, order: 4 },
          { id: 'other', name: 'Other', enabled: true, order: 5 }
        ],
        formTexts: {
          modalTitle: 'Book Consultation',
          modalSubtitle: 'Schedule your free consultation',
          step1Title: 'Choose Your Event Type',
          step2Title: 'Choose Your Service Category',
          step3Title: 'Select Date & Time',
          step4Title: 'Your Information',
          submitButtonText: 'Book Consultation',
          cancelButtonText: 'Cancel',
          successMessage: 'Consultation Booked Successfully! We\'ll contact you within 2 hours to confirm your appointment.',
          errorMessage: 'Failed to submit booking. Please try again or contact us directly.'
        },
        businessSettings: {
          businessName: 'As Easy As Rent Mini Programs',
          contactEmail: 'hello@aseasyasrentminiprograms.com',
          contactPhone: '+60 12-345 6789',
          workingDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
          minBookingDays: 1,
          maxBookingDays: 90
        },
        offForToday: false,
        timezone: 'Asia/Kuala_Lumpur',
        plannedOff: []
      };

      await this.updateBookingConfig(defaultConfig);
    } catch (error) {
      console.error('Error initializing default booking config:', error);
      throw error;
    }
  }

  // Toggle off for today
  async toggleOffForToday(): Promise<void> {
    try {
      const config = await this.getBookingConfig();
      if (!config) {
        console.error('❌ No configuration found, cannot toggle off for today');
        throw new Error('Configuration not found');
      }

      const newOffForTodayStatus = !config.offForToday;
      console.log(`🔄 Toggling off for today: ${config.offForToday} → ${newOffForTodayStatus}`);

      await this.updateBookingConfig({
        ...config,
        offForToday: newOffForTodayStatus
      });

      const malaysiaTime = new Date().toLocaleString('en-MY', { 
        timeZone: config.timezone || 'Asia/Kuala_Lumpur',
        dateStyle: 'full',
        timeStyle: 'short'
      });

      console.log(`✅ Office ${newOffForTodayStatus ? 'CLOSED' : 'OPENED'} for today (${malaysiaTime})`);
      console.log(`📅 Customers will see today's slots as ${newOffForTodayStatus ? 'unavailable/occupied' : 'available based on time'}`);
    } catch (error) {
      console.error('❌ Error toggling off for today:', error);
      throw error;
    }
  }

  // Get Malaysia time
  private getMalaysiaTime(): Date {
    const now = new Date();
    // Convert to Malaysia timezone (UTC+8)
    return new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kuala_Lumpur' }));
  }

  // Check if a date string represents today in Malaysia timezone
  isToday(dateString: string): boolean {
    if (!dateString) return false;
    const inputDate = new Date(dateString + 'T00:00:00');
    const malaysiaToday = this.getMalaysiaTime();
    return inputDate.toDateString() === malaysiaToday.toDateString();
  }
}

export const bookingService = new BookingService();
